<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="mb-8">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">Permission Tester</h1>
            <p class="mt-2 text-sm text-gray-600">
              Debug and test permission configurations for users and roles
            </p>
          </div>
          <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            Developer Tool
          </span>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Left Sidebar - User & Role Selection -->
        <div class="lg:col-span-1">
          <div class="bg-white rounded-lg shadow-sm p-6 sticky top-8">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Configuration</h3>

            <!-- User Selection -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Select User
              </label>
              <select
                v-model="selectedUserId"
                @change="loadUserData"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Select a User --</option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.name }} ({{ user.email }})
                </option>
              </select>
            </div>

            <!-- Current User Info -->
            <div v-if="selectedUser" class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 class="text-sm font-medium text-blue-900 mb-2">Current User</h4>
              <div class="space-y-1 text-sm text-blue-800">
                <p><strong>Name:</strong> {{ selectedUser.name }}</p>
                <p><strong>Email:</strong> {{ selectedUser.email }}</p>
                <p><strong>Roles:</strong> {{ actualRoles.length }}</p>
                <p><strong>Permissions:</strong> {{ actualPermissions.length }}</p>
              </div>
            </div>

            <!-- Current Roles -->
            <div v-if="actualRoles.length > 0" class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Current Roles
              </label>
              <div class="space-y-2">
                <div
                  v-for="role in actualRoles"
                  :key="role.id"
                  class="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                >
                  <span class="text-sm font-medium text-gray-700">{{ role.name }}</span>
                  <span class="text-xs text-gray-500">{{ role.permissions_count }} perms</span>
                </div>
              </div>
            </div>

            <!-- Testing Mode Toggle -->
            <div class="mb-6">
              <label class="flex items-center space-x-3">
                <input
                  v-model="whatIfMode"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span class="text-sm font-medium text-gray-700">
                  What-If Mode
                  <span class="block text-xs text-gray-500">Test without saving changes</span>
                </span>
              </label>
            </div>

            <!-- What-If Role Selection -->
            <div v-if="whatIfMode" class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Simulate Roles
              </label>
              <div class="space-y-2 max-h-64 overflow-y-auto">
                <label
                  v-for="role in availableRoles"
                  :key="role.id"
                  class="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-md cursor-pointer"
                >
                  <input
                    v-model="whatIfRoles"
                    :value="role.id"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span class="text-sm text-gray-700">{{ role.name }}</span>
                </label>
              </div>
              <div class="mt-2 text-xs text-gray-500">
                Selected: {{ whatIfRoles.length }} role(s)
              </div>
            </div>

            <!-- Reset Button -->
            <button
              v-if="whatIfMode"
              @click="resetWhatIf"
              class="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              Reset What-If
            </button>
          </div>
        </div>

        <!-- Main Content -->
        <div class="lg:col-span-2 space-y-8">
          <!-- Permission Checker -->
          <div class="bg-white rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Permission Checker</h3>

            <!-- Permission Input -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Permission to Check
              </label>
              <div class="flex space-x-2">
                <input
                  v-model="permissionToCheck"
                  type="text"
                  placeholder="e.g., system.roles.manage"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  @keyup.enter="checkPermission"
                />
                <button
                  @click="checkPermission"
                  :disabled="!permissionToCheck || !selectedUserId"
                  :class="[
                    'px-6 py-2 rounded-md font-medium transition-colors',
                    permissionToCheck && selectedUserId
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  ]"
                >
                  Check
                </button>
              </div>
              <p class="mt-1 text-xs text-gray-500">
                Enter a permission slug to test (e.g., tickets.view, system.roles.manage)
              </p>
            </div>

            <!-- Quick Check Buttons -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Quick Checks
              </label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="perm in quickCheckPermissions"
                  :key="perm"
                  @click="quickCheck(perm)"
                  class="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  {{ perm }}
                </button>
              </div>
            </div>

            <!-- Check Result -->
            <div v-if="checkResult" class="mt-4">
              <div
                :class="[
                  'p-4 rounded-md border-2',
                  checkResult.granted
                    ? 'bg-green-50 border-green-500'
                    : 'bg-red-50 border-red-500'
                ]"
              >
                <div class="flex items-start">
                  <div
                    :class="[
                      'flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center',
                      checkResult.granted ? 'bg-green-500' : 'bg-red-500'
                    ]"
                  >
                    <svg v-if="checkResult.granted" class="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                    <svg v-else class="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div class="ml-3 flex-1">
                    <h4
                      :class="[
                        'text-sm font-medium',
                        checkResult.granted ? 'text-green-900' : 'text-red-900'
                      ]"
                    >
                      {{ checkResult.granted ? 'Permission Granted' : 'Permission Denied' }}
                    </h4>
                    <p
                      :class="[
                        'mt-1 text-sm',
                        checkResult.granted ? 'text-green-800' : 'text-red-800'
                      ]"
                    >
                      {{ checkResult.reason }}
                    </p>
                    <div v-if="checkResult.grantedBy && checkResult.grantedBy.length > 0" class="mt-2">
                      <p class="text-xs font-medium text-green-900">Granted by:</p>
                      <ul class="mt-1 space-y-1">
                        <li
                          v-for="role in checkResult.grantedBy"
                          :key="role"
                          class="text-xs text-green-800"
                        >
                          • {{ role }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Effective Permissions -->
          <div class="bg-white rounded-lg shadow-sm p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-gray-900">
                Effective Permissions
                <span class="text-sm font-normal text-gray-500 ml-2">
                  ({{ effectivePermissions.length }} total)
                </span>
              </h3>
              <div class="flex space-x-2">
                <button
                  @click="groupByModule = !groupByModule"
                  :class="[
                    'px-3 py-1 text-sm rounded-md transition-colors',
                    groupByModule
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  ]"
                >
                  {{ groupByModule ? 'Grouped' : 'List View' }}
                </button>
                <button
                  @click="exportPermissions"
                  class="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Export
                </button>
              </div>
            </div>

            <!-- Search Box -->
            <div class="mb-4">
              <input
                v-model="permissionSearch"
                type="text"
                placeholder="Search permissions..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <!-- Grouped View -->
            <div v-if="groupByModule && selectedUserId" class="space-y-4">
              <div
                v-for="(perms, module) in groupedPermissions"
                :key="module"
                class="border border-gray-200 rounded-md"
              >
                <button
                  @click="toggleModule(module)"
                  class="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <span class="font-medium text-gray-900">
                    {{ formatModuleName(module) }}
                    <span class="text-sm font-normal text-gray-500 ml-2">({{ perms.length }})</span>
                  </span>
                  <svg
                    :class="['h-5 w-5 text-gray-500 transition-transform', expandedModules.includes(module) ? 'transform rotate-180' : '']"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-show="expandedModules.includes(module)" class="p-3 space-y-1">
                  <div
                    v-for="perm in perms"
                    :key="perm"
                    class="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                  >
                    <span class="text-sm text-gray-700">{{ perm }}</span>
                    <button
                      @click="quickCheck(perm)"
                      class="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Test
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- List View -->
            <div v-else-if="selectedUserId" class="space-y-1 max-h-96 overflow-y-auto">
              <div
                v-for="perm in filteredPermissions"
                :key="perm"
                class="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
              >
                <span class="text-sm text-gray-700">{{ perm }}</span>
                <button
                  @click="quickCheck(perm)"
                  class="text-xs text-blue-600 hover:text-blue-800"
                >
                  Test
                </button>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-12">
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <h3 class="mt-2 text-sm font-medium text-gray-900">No user selected</h3>
              <p class="mt-1 text-sm text-gray-500">
                Select a user to view their effective permissions
              </p>
            </div>
          </div>

          <!-- Role Simulator -->
          <div class="bg-white rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Role Simulator</h3>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Select Multiple Roles to Simulate
              </label>
              <div class="grid grid-cols-2 gap-2">
                <label
                  v-for="role in availableRoles"
                  :key="role.id"
                  class="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    v-model="simulatedRoles"
                    :value="role.id"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span class="text-sm text-gray-700">{{ role.name }}</span>
                </label>
              </div>
            </div>

            <!-- Simulated Permissions -->
            <div v-if="simulatedRoles.length > 0" class="mt-4">
              <h4 class="text-sm font-medium text-gray-700 mb-2">
                Resulting Permissions: {{ simulatedPermissions.length }}
              </h4>
              <div class="p-4 bg-blue-50 border border-blue-200 rounded-md max-h-64 overflow-y-auto">
                <div class="grid grid-cols-2 gap-2">
                  <span
                    v-for="perm in simulatedPermissions"
                    :key="perm"
                    class="text-xs text-blue-800"
                  >
                    {{ perm }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Conflict Detection -->
            <div v-if="conflicts.length > 0" class="mt-4">
              <h4 class="text-sm font-medium text-red-700 mb-2">
                Conflicts Detected: {{ conflicts.length }}
              </h4>
              <div class="p-4 bg-red-50 border border-red-200 rounded-md">
                <ul class="space-y-1">
                  <li
                    v-for="conflict in conflicts"
                    :key="conflict"
                    class="text-xs text-red-800"
                  >
                    • {{ conflict }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { router } from '@inertiajs/vue3';

// Props
const props = defineProps({
  users: {
    type: Array,
    required: true,
  },
  roles: {
    type: Array,
    required: true,
  },
  allPermissions: {
    type: Array,
    required: true,
  },
});

// State
const selectedUserId = ref('');
const selectedUser = ref(null);
const actualRoles = ref([]);
const actualPermissions = ref([]);
const whatIfMode = ref(false);
const whatIfRoles = ref([]);
const permissionToCheck = ref('');
const checkResult = ref(null);
const permissionSearch = ref('');
const groupByModule = ref(true);
const expandedModules = ref([]);
const simulatedRoles = ref([]);
const availableRoles = ref(props.roles || []);

// Quick check permissions
const quickCheckPermissions = [
  'system.roles.manage',
  'system.users.manage',
  'tickets.view',
  'tickets.create',
  'tickets.edit',
  'tickets.delete',
];

// Computed
const effectivePermissions = computed(() => {
  if (!selectedUserId.value) return [];

  if (whatIfMode.value && whatIfRoles.value.length > 0) {
    // Calculate permissions from what-if roles
    return calculatePermissionsFromRoles(whatIfRoles.value);
  }

  return actualPermissions.value;
});

const filteredPermissions = computed(() => {
  if (!permissionSearch.value) return effectivePermissions.value;

  const query = permissionSearch.value.toLowerCase();
  return effectivePermissions.value.filter(perm =>
    perm.toLowerCase().includes(query)
  );
});

const groupedPermissions = computed(() => {
  const grouped = {};

  filteredPermissions.value.forEach(perm => {
    const parts = perm.split('.');
    const module = parts[0] || 'other';

    if (!grouped[module]) {
      grouped[module] = [];
    }

    grouped[module].push(perm);
  });

  return grouped;
});

const simulatedPermissions = computed(() => {
  if (simulatedRoles.value.length === 0) return [];
  return calculatePermissionsFromRoles(simulatedRoles.value);
});

const conflicts = computed(() => {
  // Detect potential conflicts in simulated roles
  // This is a simplified example - implement actual conflict detection logic
  return [];
});

// Methods
const loadUserData = async () => {
  if (!selectedUserId.value) {
    selectedUser.value = null;
    actualRoles.value = [];
    actualPermissions.value = [];
    checkResult.value = null;
    return;
  }

  // Find selected user
  selectedUser.value = props.users.find(u => u.id == selectedUserId.value);

  // Load user roles and permissions via API
  try {
    const response = await axios.get(`/settings/users/${selectedUserId.value}/roles/effective-permissions`);

    actualRoles.value = response.data.roles || [];
    actualPermissions.value = response.data.permissions || [];
  } catch (error) {
    console.error('Error loading user data:', error);
    actualRoles.value = [];
    actualPermissions.value = [];
  }
};

const checkPermission = () => {
  if (!permissionToCheck.value || !selectedUserId.value) return;

  const hasPermission = effectivePermissions.value.includes(permissionToCheck.value);

  if (hasPermission) {
    // Find which roles grant this permission
    const grantingRoles = actualRoles.value
      .filter(role => {
        // Check if this role has the permission
        const rolePerms = getRolePermissions(role.id);
        return rolePerms.includes(permissionToCheck.value);
      })
      .map(role => role.name);

    checkResult.value = {
      granted: true,
      reason: whatIfMode.value
        ? `Permission would be granted in What-If scenario`
        : `User has permission through their assigned roles`,
      grantedBy: grantingRoles,
    };
  } else {
    checkResult.value = {
      granted: false,
      reason: whatIfMode.value
        ? `Permission would NOT be granted in What-If scenario`
        : `User does not have this permission`,
      grantedBy: [],
    };
  }
};

const quickCheck = (permission) => {
  permissionToCheck.value = permission;
  checkPermission();
};

const calculatePermissionsFromRoles = (roleIds) => {
  const permissions = new Set();

  roleIds.forEach(roleId => {
    const role = availableRoles.value.find(r => r.id === roleId);
    if (role && role.permissions) {
      role.permissions.forEach(perm => permissions.add(perm));
    }
  });

  return Array.from(permissions).sort();
};

const getRolePermissions = (roleId) => {
  const role = availableRoles.value.find(r => r.id === roleId);
  return role && role.permissions ? role.permissions : [];
};

const resetWhatIf = () => {
  whatIfRoles.value = [];
  checkResult.value = null;
};

const toggleModule = (module) => {
  const index = expandedModules.value.indexOf(module);
  if (index > -1) {
    expandedModules.value.splice(index, 1);
  } else {
    expandedModules.value.push(module);
  }
};

const formatModuleName = (module) => {
  return module
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const exportPermissions = () => {
  const data = JSON.stringify({
    user: selectedUser.value,
    permissions: effectivePermissions.value,
    roles: actualRoles.value,
    timestamp: new Date().toISOString(),
  }, null, 2);

  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `permissions-${selectedUser.value?.email || 'user'}-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// Watch for changes
watch(whatIfMode, (newValue) => {
  if (!newValue) {
    whatIfRoles.value = [];
    checkResult.value = null;
  }
});

// Initialize
if (props.users.length === 1) {
  selectedUserId.value = props.users[0].id;
  loadUserData();
}
</script>
